import React from 'react';
import { MessageList as OriginList, Message, TypingIndicator, MessageProps } from '@chatscope/chat-ui-kit-react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import useStyles from './styles';
import { getMessageId } from '../../containers/Widget/helpers';
import { Tooltip } from '@material-ui/core';
import * as Icon from '../Icon';
import { Texts } from '../../constants';
import copy from 'copy-to-clipboard';
import { apiCall } from '../../utils';
import { WidgetProps } from '../../containers/Widget/types';
import { Components } from 'react-markdown';
import ActionButton from '../ActionButton';

interface MessageListProps {
  messages: MessageProps[]
  isLoading?: boolean
  onMessageRegenerate: (index: number) => void
  widgetProps: WidgetProps
}

const MessageList: React.FC<MessageListProps> = ({
  messages,
  isLoading,
  onMessageRegenerate,
  widgetProps,
}) => {
  const classes = useStyles();
  const [thumbUpIconMouseHover, setThumbUpIconMouseHover] = React.useState(false);
  const [thumbDownIconMouseHover, setThumbDownIconMouseHover] = React.useState(false);

  const handleCopyClick: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    const i = Number(event.currentTarget.getAttribute('data-index'));
    const text = messages[i].model?.message;

    if (text) {
      copy(text);
    }
  }

  const sendMessageFeedback = async (event: React.MouseEvent<HTMLButtonElement>, rating: 1 | -1) => {
    const i = Number(event.currentTarget.getAttribute('data-index'));
    const messageId = getMessageId(messages[i]);
    const { appName, apiPath, token } = widgetProps;

    try {
      await apiCall({
        url: `${apiPath}/message_feedback`,
        method: 'POST',
        token,
        payload: {
          message_id: messageId,
          application_id: appName,
          rating,
        }
      });
    } catch (error) {
      console.error(error);
    }
  }

  const handleGoodFeedback: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    return sendMessageFeedback(event, 1);
  }

  const handleBadFeedback: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    return sendMessageFeedback(event, -1);
  }

  const handleRegenerateClick: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    const i = Number(event.currentTarget.getAttribute('data-index'));
    onMessageRegenerate(i);
  }

  const components: Components = {
    a: ({ href, children }) => (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={classes.linkComponents}
      >
        {children}
      </a>
    ),
  };

  return (
    <OriginList
      className={classes.list}
      typingIndicator={isLoading && <TypingIndicator className={classes.typing} data-cy="loading-indicator" />}
    >
      {messages.map((message, i) => {
        const isIncoming = message.model?.direction === 'incoming';
        const isFirst = i === 0; // initial message
        const allowActions = isIncoming && !isFirst;

        return (
          <Message
            key={getMessageId(message) ?? i}
            className={isIncoming ? classes.incomingMessage : classes.outgoingMessage}
            data-cy={`message-${i}`}
            {...message}
          >
            {message.type === 'text' && (
              <Message.TextContent>
                <div className={classes.textContent}>
                  <Markdown remarkPlugins={[remarkGfm]} components={components}>
                    {message.model?.message ?? ''}
                  </Markdown>
                </div>
                {allowActions && (
                  <ActionButton
                    tooltip={Texts.Copy}
                    className={classes.messageButton}
                    data-index={i}
                    onClick={handleCopyClick}
                    icon={Icon.Copy}
                    data-cy="copy-response"
                  />
                )}
                {allowActions && (
                  <ActionButton
                    tooltip={Texts.Regenerate}
                    className={classes.messageButton}
                    data-index={i}
                    onClick={handleRegenerateClick}
                    disabled={isLoading}
                    icon={Icon.RotateRight}
                    data-cy="regenerate-response"
                  />
                )}
                {allowActions && (
                  <ActionButton
                    tooltip={Texts.GoodResponse}
                    className={classes.messageButton}
                    data-index={i}
                    onClick={handleGoodFeedback}
                    onMouseOver={() => setThumbUpIconMouseHover(true)}
                    onMouseLeave={() => setThumbUpIconMouseHover(false)}
                    icon={thumbUpIconMouseHover ? Icon.ThumbUpSolid : Icon.ThumbUpRegular}
                    data-cy="good-response"
                  />
                )}
                {allowActions && (
                  <ActionButton
                    tooltip={Texts.BadResponse}
                    className={classes.messageButton}
                    data-index={i}
                    onClick={handleBadFeedback}
                    onMouseOver={() => setThumbDownIconMouseHover(true)}
                    onMouseLeave={() => setThumbDownIconMouseHover(false)}
                    icon={thumbDownIconMouseHover ? Icon.ThumbDownSolid : Icon.ThumbDownRegular}
                    data-cy="bad-response"
                  />
                )}
              </Message.TextContent>
            )}
          </Message>
        )
      })}
    </OriginList>
  );
}

export default MessageList;
